/*
 * discover-map.js — shared Google Maps (vector) for every Discover map section.
 * Replaces the old per-section Leaflet/OSM maps. One asset drives all three:
 *   - WTS     #where-to-see-map    multi-pin + cluster; popup: works + "View works" (scroll-to-card) + directions
 *   - Museum  #museum-location-map  single pin; popup: name (open by default)
 *   - City    #city-museums-map     multi-pin + cluster + movement filters; popup: artists + details + Google/Apple directions
 *
 * Each map lazy-inits when it scrolls into view (perf + only spends a Maps load
 * when seen, and avoids blank WebGL init inside the dc-reveal hidden state).
 *
 * The browser key + Map ID are injected at render time as window.SOLIS_MAPS_KEY
 * / window.SOLIS_MAPS_ID by snippets/discover-maps-config.liquid (sourced from
 * the shop.metafields.solis.google_maps_key metafield) so the literal is not
 * committed to source. The key is HTTP-referrer-restricted to the .co.uk and
 * .com storefronts, restricted to the Maps JavaScript API, and quota-capped at
 * 2,000 map-loads/day. If no key is present the maps degrade gracefully
 * (placeholder / touch fallback, no JS errors). Map ID drives branded markers
 * + cloud base-map styling.
 * Setup + gotchas: docs/modules/discovery.md ("Discover maps — Google Maps").
 */
(function () {
  'use strict';

  // Injected by snippets/discover-maps-config.liquid (shop metafield) — see header.
  var MAPS_KEY = (typeof window !== 'undefined' && window.SOLIS_MAPS_KEY) || '';
  var MAP_ID = (typeof window !== 'undefined' && window.SOLIS_MAPS_ID) || '129205160ae0525f764bcbfd';
  var CLUSTERER_SRC = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';

  // Discover theme strings (i18n chunk 10, 2026-09-26). snippets/discover-i18n.liquid
  // sets window.SOLIS_DISCOVER_I18N from locales/*.json (solis_discover.js.*).
  // Every call passes the English text, used when the snippet or a key is
  // missing, so English output is unchanged. %n stands for a number.
  var DC_I18N = (typeof window !== 'undefined' && window.SOLIS_DISCOVER_I18N) || {};
  function dcT(key, english) {
    var v = DC_I18N[key];
    return (typeof v === 'string' && v && v.indexOf('translation missing') === -1) ? v : english;
  }
  function dcTn(key, n, englishOne, englishOther) {
    var cat;
    try { cat = new Intl.PluralRules(DC_I18N.locale || 'en').select(n); } catch (e) { cat = n === 1 ? 'one' : 'other'; }
    return (cat === 'one' ? dcT(key + '_one', englishOne) : dcT(key + '_other', englishOther)).replace('%n', String(n));
  }

  // ---- Google Maps JS loader (once, on first map that scrolls into view) ----
  var apiPromise = null;
  function loadMapsApi() {
    if (window.google && window.google.maps && window.google.maps.marker) return Promise.resolve();
    // No key configured (metafield unset) → reject so the caller leaves the
    // placeholder/touch fallback in place instead of loading Maps with a blank key.
    if (!MAPS_KEY) return Promise.reject(new Error('Google Maps key not configured (window.SOLIS_MAPS_KEY)'));
    if (apiPromise) return apiPromise;
    apiPromise = new Promise(function (resolve, reject) {
      window.__discoverMapsReady = function () { resolve(); };
      var s = document.createElement('script');
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + MAPS_KEY +
        '&libraries=marker&v=weekly&loading=async&callback=__discoverMapsReady' +
        // Map labels in the page's language on a locale page; English pages keep no parameter.
        (DC_I18N.locale && DC_I18N.locale !== 'en' ? '&language=' + encodeURIComponent(DC_I18N.locale) : '');
      s.async = true;
      s.onerror = function () { reject(new Error('Google Maps failed to load')); };
      document.head.appendChild(s);
    });
    return apiPromise;
  }

  // ---- markerclusterer loader (once) ----
  var clustererPromise = null;
  function loadClusterer() {
    if (window.markerClusterer) return Promise.resolve(window.markerClusterer);
    if (clustererPromise) return clustererPromise;
    clustererPromise = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = CLUSTERER_SRC;
      s.onload = function () { resolve(window.markerClusterer || null); };
      s.onerror = function () { resolve(null); };
      document.head.appendChild(s);
    });
    return clustererPromise;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // ---- analytics ----
  // Self-contained mirror of discover.js's pushAnalytics. Popup LINKS carry
  // `data-dc-track` and are caught by discover.js's delegated handler, but a
  // marker click is a Maps JS event on a non-DOM overlay, so it can't bubble to
  // that handler — emit it directly here. Param names match the registered GA4
  // custom dimensions (`dc_section` / `dc_item`); keep them in sync with
  // discover.js. Fires even under GTM (no GTM tag exists for discover_* events).
  function trackMap(eventName, section, item) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ event: eventName, dc_section: section, dc_item: item });
      }
    } catch (e) { /* no-op */ }
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, { dc_section: section, dc_item: item });
      }
    } catch (e) { /* no-op */ }
  }

  function brandedPin() {
    return new google.maps.marker.PinElement({
      background: '#26736b', borderColor: '#1c5249', glyphColor: '#ffffff', scale: 1.1
    }).element;
  }

  // Branded cluster bubbles — teal circle sized by count, white count (5.4:1 AA),
  // replacing markerclusterer's default blue. render() runs after the API loads.
  var clusterRenderer = {
    render: function (cluster) {
      var count = cluster.count;
      var size = count < 10 ? 36 : (count < 30 ? 44 : 52);
      var el = document.createElement('div');
      el.style.cssText = 'width:' + size + 'px;height:' + size + 'px;border-radius:50%;' +
        'background:#26736b;color:#fff;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.3);' +
        'display:flex;align-items:center;justify-content:center;font-weight:600;font-size:13px;line-height:1;';
      el.textContent = String(count);
      return new google.maps.marker.AdvancedMarkerElement({ position: cluster.position, content: el, zIndex: 1000 + count });
    }
  };

  function isCoarsePointer() {
    return !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  }

  function renderTouchFallback(el, kind) {
    if (!el || el.dataset.gmapBuilt) return true;
    el.dataset.gmapBuilt = 'touch-fallback';
    el.classList.add('dc-wts-map--touch-fallback');
    var museums = kind === 'museum' ? [] : parseMuseums(el).filter(function (m) { return m.lat && m.lon; });
    var count = museums.length;
    var title = kind === 'museum' ? (el.getAttribute('data-name') || dcT('map_museum_location', 'Museum location')) : dcT('map_museum_locations', 'Museum locations');
    var detail = count
      ? dcTn('map_location', count, '%n location in the museum list below.', '%n locations in the museum list below.')
      : dcT('map_location_none', 'Location details are available in the page content below.');
    el.innerHTML = '<div class="dc-wts-map__touch-fallback">' +
      '<strong class="dc-wts-map__touch-title">' + esc(title) + '</strong>' +
      '<span class="dc-wts-map__touch-detail">' + esc(detail) + '</span>' +
      '</div>';
    return true;
  }

  function newMap(el, extra) {
    // Desktop keeps an interactive Google map. Touch devices do not auto-hydrate
    // Maps; see lazy() below. iOS WebKit can leave one-finger page scroll captured
    // by the Maps runtime even with gestureHandling:'none'.
    var opts = {
      mapId: MAP_ID,
      gestureHandling: 'cooperative',
      zoomControl: true,
      mapTypeControl: false, streetViewControl: false,
      fullscreenControl: true, clickableIcons: false
    };
    if (extra) for (var k in extra) opts[k] = extra[k];
    return new google.maps.Map(el, opts);
  }

  function parseMuseums(el) {
    var raw = el.getAttribute('data-museums');
    if (!raw) return [];
    try { return JSON.parse(raw); } catch (e) { return []; }
  }

  // ---- WTS click-to-card (delegated once) ----
  var cardDelegated = false;
  function ensureCardDelegation() {
    if (cardDelegated) return;
    cardDelegated = true;
    document.addEventListener('click', function (ev) {
      var link = ev.target.closest && ev.target.closest('[data-wts-scroll]');
      if (!link) return;
      ev.preventDefault();
      var card = document.getElementById('wts-museum-' + link.getAttribute('data-wts-scroll'));
      if (!card) return;
      // If the card lives inside a collapsed "View all" <details>, open it first
      // so the scroll lands on a visible element (no dead pins for tail museums).
      var det = card.closest && card.closest('details');
      if (det && !det.open) det.open = true;
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card.classList.remove('dc-museum-card--ping');
      void card.offsetWidth; // reflow so the animation restarts on repeat clicks
      card.classList.add('dc-museum-card--ping');
    });
  }

  // ---- popup builders ----
  function wtsPopup(m) {
    var h = '<div class="dc-map-pop"><div class="dc-map-pop__name">' + esc(m.name || dcT('map_museum', 'Museum')) + '</div>';
    if (m.city) h += '<div class="dc-map-pop__city">' + esc(m.city) + '</div>';
    if (m.count) h += '<div class="dc-map-pop__count">' + dcTn('map_work', parseInt(m.count, 10) || 0, '%n work', '%n works') + '</div>';
    h += '<div class="dc-map-pop__actions">';
    if (m.id) h += '<a class="dc-map-pop__link" data-wts-scroll="' + esc(m.id) + '" href="#wts-museum-' + esc(m.id) + '" data-dc-track="wts-map" data-dc-item="map-view-works" data-dc-event="discover_map_view_works">' + dcT('map_view_works', 'View works ↓') + '</a>';
    var dest = encodeURIComponent((m.name || '') + (m.city ? ', ' + m.city : ''));
    h += '<a class="dc-map-pop__link dc-map-pop__link--dir" target="_blank" rel="noopener noreferrer" href="https://www.google.com/maps/dir/?api=1&destination=' + dest + '" data-dc-track="wts-map" data-dc-item="map-directions-google" data-dc-event="discover_travel_click">' + dcT('map_directions', 'Get directions ↗') + '</a>';
    return h + '</div></div>';
  }

  function cityPopup(m) {
    var h = '<div class="dc-map-pop"><div class="dc-map-pop__name">' + esc(m.name || dcT('map_museum', 'Museum')) + '</div>';
    if (m.count) h += '<div class="dc-map-pop__count">' + dcTn('map_artist', parseInt(m.count, 10) || 0, '%n artist', '%n artists') + '</div>';
    if (m.movements && m.movements.length) h += '<div class="dc-map-pop__city">' + m.movements.slice(0, 3).map(esc).join(', ') + '</div>';
    h += '<div class="dc-map-pop__actions">';
    if (m.slug) h += '<a class="dc-map-pop__link" href="' + esc(window.SolisMarketPath('/pages/museum-' + m.slug)) + '" data-dc-track="city-map" data-dc-item="map-details-' + encodeURIComponent(m.slug) + '">' + dcT('map_details', 'Details →') + '</a>';
    if (m.googleDirectionsUrl) h += '<a class="dc-map-pop__link dc-map-pop__link--dir" target="_blank" rel="noopener" href="' + esc(m.googleDirectionsUrl) + '" data-dc-track="city-map" data-dc-item="map-directions-google" data-dc-event="discover_travel_click">' + dcT('map_google_directions', 'Google directions ↗') + '</a>';
    if (m.appleDirectionsUrl) h += '<a class="dc-map-pop__link dc-map-pop__link--dir" target="_blank" rel="noopener" href="' + esc(m.appleDirectionsUrl) + '" data-dc-track="city-map" data-dc-item="map-directions-apple" data-dc-event="discover_travel_click">' + dcT('map_apple_maps', 'Apple Maps ↗') + '</a>';
    return h + '</div></div>';
  }

  // ---- multi-pin map (WTS + city) ----
  function buildMulti(el, kind) {
    var museums = parseMuseums(el).filter(function (m) { return m.lat && m.lon; });
    if (!museums.length) { el.style.display = 'none'; return Promise.resolve(); }
    return loadMapsApi().then(function () {
      el.innerHTML = '';
      var map = newMap(el);
      var info = new google.maps.InfoWindow({ maxWidth: 280 });
      var bounds = new google.maps.LatLngBounds();
      var buildPopup = kind === 'city' ? cityPopup : wtsPopup;
      if (kind === 'wts') ensureCardDelegation();

      var entries = museums.map(function (m) {
        var pos = { lat: Number(m.lat), lng: Number(m.lon) };
        bounds.extend(pos);
        var marker = new google.maps.marker.AdvancedMarkerElement({
          position: pos, title: m.name || dcT('map_museum', 'Museum'), content: brandedPin(), gmpClickable: true
        });
        marker.addListener('gmp-click', function () {
          info.setContent(buildPopup(m));
          info.open({ anchor: marker, map: map });
          trackMap('discover_map_open', kind === 'city' ? 'city-map' : 'wts-map', 'map-pin');
        });
        return { m: m, marker: marker };
      });

      var maxZoom = kind === 'city' ? 15 : 12;
      if (museums.length === 1) { map.setCenter(bounds.getCenter()); map.setZoom(maxZoom); }
      else {
        map.fitBounds(bounds, 48);
        google.maps.event.addListenerOnce(map, 'idle', function () {
          if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
        });
      }

      return loadClusterer().then(function (mc) {
        var cluster = null;
        if (mc && mc.MarkerClusterer) {
          cluster = new mc.MarkerClusterer({ map: map, renderer: clusterRenderer });
          cluster.addMarkers(entries.map(function (e) { return e.marker; }));
        } else {
          entries.forEach(function (e) { e.marker.map = map; });
        }
        function show(list) {
          if (cluster) {
            cluster.clearMarkers();
            cluster.addMarkers(list.map(function (e) { return e.marker; }));
          } else {
            entries.forEach(function (e) { e.marker.map = null; });
            list.forEach(function (e) { e.marker.map = map; });
          }
        }
        if (kind === 'city') addCityFilters(el, entries, show);
      });
    });
  }

  // ---- city movement-filter buttons (ported from the old Leaflet map) ----
  function addCityFilters(el, entries, show) {
    var counts = {};
    entries.forEach(function (e) {
      (e.m.movements || []).forEach(function (mv) { counts[mv] = (counts[mv] || 0) + 1; });
    });
    var movements = Object.keys(counts)
      .filter(function (n) { return counts[n] >= 2; })
      .sort(function (a, b) { return counts[b] - counts[a] || a.localeCompare(b); })
      .slice(0, 8);
    if (movements.length < 3) return;

    var controls = document.createElement('div');
    controls.className = 'dc-map-filters';
    controls.setAttribute('aria-label', dcT('map_filter_label', 'Filter map by movement'));
    function btn(label, mv, active) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'dc-map-filter' + (active ? ' is-active' : '');
      b.textContent = label;
      b.setAttribute('data-movement', mv);
      b.setAttribute('data-dc-track', 'city-map');
      b.setAttribute('data-dc-item', 'map-filter-' + (mv === '__all' ? 'all' : mv.toLowerCase().replace(/[^a-z0-9]+/g, '-')));
      b.setAttribute('data-dc-event', 'discover_map_filter');
      return b;
    }
    controls.appendChild(btn(dcT('map_filter_all', 'All'), '__all', true));
    movements.forEach(function (mv) { controls.appendChild(btn(mv, mv, false)); });
    controls.addEventListener('click', function (ev) {
      var b = ev.target.closest && ev.target.closest('button[data-movement]');
      if (!b) return;
      var all = controls.querySelectorAll('.dc-map-filter');
      for (var i = 0; i < all.length; i++) all[i].classList.remove('is-active');
      b.classList.add('is-active');
      var mv = b.getAttribute('data-movement');
      show(mv === '__all' ? entries : entries.filter(function (e) { return (e.m.movements || []).indexOf(mv) !== -1; }));
    });
    el.parentNode.insertBefore(controls, el);
  }

  // ---- single-pin map (museum) ----
  function buildMuseum(el) {
    var lat = parseFloat(el.getAttribute('data-lat'));
    var lon = parseFloat(el.getAttribute('data-lon'));
    var name = el.getAttribute('data-name') || dcT('map_museum', 'Museum');
    if (!lat || !lon) { el.style.display = 'none'; return Promise.resolve(); }
    return loadMapsApi().then(function () {
      el.innerHTML = '';
      var pos = { lat: lat, lng: lon };
      var map = newMap(el, { center: pos, zoom: 15 });
      var marker = new google.maps.marker.AdvancedMarkerElement({
        position: pos, title: name, content: brandedPin(), gmpClickable: true, map: map
      });
      var info = new google.maps.InfoWindow({
        maxWidth: 280, content: '<div class="dc-map-pop"><div class="dc-map-pop__name">' + esc(name) + '</div></div>'
      });
      info.open({ anchor: marker, map: map });
      marker.addListener('gmp-click', function () { info.open({ anchor: marker, map: map }); trackMap('discover_map_open', 'museum-map', 'map-pin'); });
    });
  }

  // ---- lazy-init wrapper ----
  function lazy(el, build, kind) {
    if (!el || el.dataset.gmapBuilt) return;
    if (isCoarsePointer()) { renderTouchFallback(el, kind); return; }
    function run() {
      if (el.dataset.gmapBuilt) return;
      el.dataset.gmapBuilt = '1';
      Promise.resolve(build(el)).catch(function () { /* leave the placeholder on failure */ });
    }
    if (typeof IntersectionObserver === 'undefined') { run(); return; }
    var io = new IntersectionObserver(function (entries, obs) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { obs.disconnect(); run(); break; }
      }
    }, { rootMargin: '200px 0px' });
    io.observe(el);
  }

  function init() {
    lazy(document.getElementById('where-to-see-map'), function (el) { return buildMulti(el, 'wts'); }, 'wts');
    lazy(document.getElementById('city-museums-map'), function (el) { return buildMulti(el, 'city'); }, 'city');
    lazy(document.getElementById('museum-location-map'), buildMuseum, 'museum');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
